import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../api/auth";
import { AuthContext } from "./authContextInstance";
/**
 * Shared AuthProvider. Loads the current manager on mount (if a token exists),
 * exposes the canonical AuthContextType, and persists tokens to localStorage —
 * the same plumbing the per-app providers carried, consolidated to one source.
 */
export function AuthProvider({ client, children, logoutRedirect = "/", }) {
    const [manager, setManager] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        const loadManager = async () => {
            const token = localStorage.getItem(ACCESS_TOKEN_KEY);
            if (!token) {
                if (!cancelled)
                    setLoading(false);
                return;
            }
            try {
                const current = await client.getCurrentManager();
                if (!cancelled)
                    setManager(current);
            }
            catch (error) {
                console.error("Failed to load manager:", error);
                localStorage.removeItem(ACCESS_TOKEN_KEY);
                localStorage.removeItem(REFRESH_TOKEN_KEY);
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        };
        loadManager();
        return () => {
            cancelled = true;
        };
    }, [client]);
    const login = useCallback(async (credentials) => {
        const { manager: loggedIn, tokens } = await client.loginManager(credentials);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        setManager(loggedIn);
    }, [client]);
    const register = useCallback(async (data) => {
        const { manager: registered, tokens } = await client.registerManager(data);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        setManager(registered);
    }, [client]);
    const loginWithGoogle = useCallback(async (credential) => {
        const { manager: loggedIn, tokens } = await client.loginWithGoogle(credential);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        setManager(loggedIn);
    }, [client]);
    const registerWithGoogle = useCallback(async (credential) => {
        const { manager: registered, tokens } = await client.registerWithGoogle(credential);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        setManager(registered);
    }, [client]);
    const logout = useCallback(() => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setManager(null);
        window.location.href = logoutRedirect;
    }, [logoutRedirect]);
    const updateManager = useCallback((updated) => {
        setManager(updated);
    }, []);
    const value = useMemo(() => ({
        manager,
        loading,
        login,
        register,
        loginWithGoogle,
        registerWithGoogle,
        logout,
        updateManager,
    }), [
        manager,
        loading,
        login,
        register,
        loginWithGoogle,
        registerWithGoogle,
        logout,
        updateManager,
    ]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
